import Logger from "../../../clients/sentry";
import { dataVizColorArray } from "../../styles/colors";
import { NotFoundError } from "../../../errors";
import { getColumnLetter } from "../../workbook/columns";
import { workbookCache } from "../../workbook/cache";
import { CHART_ASPECT_RATIOS, ChartOption, ChartShape, ChartSize } from "../api";

const CHART_BORDER_COLOR = "#AEAEAE";
export const CHART_PREFIX = "_";

export const pruneAbandonedChartTables = async (linkedTableNames: string[]) => {
  if (!linkedTableNames || linkedTableNames.length === 0) {
    console.warn("Table pruning skipped: No linked table names provided.");
    return;
  }

  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const tables = workbook.tables;
    tables.load("items/name");
    await context.sync();

    /*for (const table of tables.items) {
      if (table.name.startsWith(CHART_PREFIX) && !linkedTableNames.includes(table.name)) {
        const worksheet = table.worksheet;
        worksheet.load("name");
        await context.sync();

        worksheet.delete();
      }
    }*/
    // Log the need to delete, but do not implement actual pruning
    const tablesToDelete = tables.items.filter(
      (table) => table.name.startsWith(CHART_PREFIX) && !linkedTableNames.includes(table.name)
    );
    Logger.warn("Chart tables need to be deleted", { count: tablesToDelete.length, tables: tablesToDelete });
  });
};

export const getNextChartIndex = async (): Promise<string> => {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const tables = workbook.tables;
    tables.load("items/name");
    await context.sync();

    let maxIndex = 0;
    for (const table of tables.items) {
      if (table.name.startsWith(CHART_PREFIX)) {
        const index = parseInt(table.name.split("_")[2]);
        if (!isNaN(index) && index > maxIndex) {
          maxIndex = index;
        }
      }
    }
    const nextIndex = String(maxIndex + 1).padStart(4, "0");
    return nextIndex;
  });
};

export const getChartDimensions = (shape: ChartShape, size?: ChartSize) => {
  const fullHeight = 700;
  const chartHeight =
    size === "full"
      ? fullHeight
      : size === "large"
        ? Math.floor(fullHeight * 0.75)
        : size === "small"
          ? Math.floor(fullHeight * 0.33)
          : Math.floor(fullHeight * 0.5); // default to medium

  const chartWidth = Math.floor(chartHeight * CHART_ASPECT_RATIOS[shape || "landscape"]);
  return { width: chartWidth, height: chartHeight };
};

export const colorizeChart = async (
  chart: Excel.Chart,
  context: Excel.RequestContext,
  colorizeSingleSeries: boolean = true
) => {
  //check useEdiphiColors
  const useEdiphiColors = (await workbookCache.get("useEdiphiColors")) ?? true;
  if (!useEdiphiColors) {
    return;
  }
  // 1) Find out how many series there are
  const seriesCountResult = chart.series.getCount();
  await context.sync();

  if (seriesCountResult.value === 1 && colorizeSingleSeries) {
    // Single‐series chart: color each POINT (slice) in that lone series
    const onlySeries = chart.series.getItemAt(0);
    const pointCountResult = onlySeries.points.getCount();
    await context.sync();

    for (let i = 0; i < pointCountResult.value; i++) {
      onlySeries.points.getItemAt(i).format.fill.setSolidColor(dataVizColorArray[i % dataVizColorArray.length]);
    }
  } else {
    // Multi‐series chart: color each SERIES
    for (let i = 0; i < seriesCountResult.value; i++) {
      chart.series.getItemAt(i).format.fill.setSolidColor(dataVizColorArray[i % dataVizColorArray.length]);
    }
  }

  // 2) Add a uniform border
  chart.format.border.color = CHART_BORDER_COLOR;
  chart.format.border.weight = 1;

  // 3) Write everything back in one final sync
  await context.sync();
};

export const splitChartTitle = (titleText: string | undefined): { title: string; subTitle: string } => {
  const [title, subTitle] = titleText ? titleText.split("\n") : [];
  return { title: title || "Untitled Chart", subTitle: subTitle || "" };
};

export const setChartTitleAndSubtitle = (chart: Excel.Chart, mainTitle: string, subTitle: string): void => {
  const fullText = `${mainTitle}\n${subTitle}`;
  chart.title.text = fullText;
  chart.title.format.font.size = 16;
  const nlPos = fullText.indexOf("\n");
  if (nlPos > -1) {
    chart.title.getSubstring(0, nlPos).font.bold = true;
    chart.title.getSubstring(nlPos + 1, fullText.length - nlPos - 1).font.bold = false;
    chart.title.getSubstring(nlPos + 1, fullText.length - nlPos - 1).font.italic = true;
    chart.title.getSubstring(nlPos + 1, fullText.length - nlPos - 1).font.size = 14;
  } else {
    chart.title.format.font.bold = true;
  }
};

export const getChartAndTable = async (
  context: Excel.RequestContext,
  worksheetName: string,
  chartName: string
): Promise<[Excel.Chart, Excel.Table]> => {
  const sheet = context.workbook.worksheets.getItem(worksheetName);
  sheet.load("charts");
  await context.sync();
  const chart = sheet.charts.getItemOrNullObject(chartName);
  const table = context.workbook.tables.getItemOrNullObject(chartName);
  await context.sync();

  if (!chart) throw new NotFoundError("Chart not found", { chartName });
  if (table.isNullObject) throw new NotFoundError("Table not found", { tableName: chartName });

  return [chart, table];
};

export async function upsertChartTable(
  rows: Record<string, string | number | boolean | Date | null | undefined>[],
  tableName: string,
  leftColumnsAsText: number = 0 //the number of left most columns to treat as text
) {
  const prefix = "__";
  let sheetName: string = prefix + tableName;
  const startCell: string = "A1";
  if (rows.length === 0) {
    return;
  }

  try {
    await Excel.run(async (context) => {
      let sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
      let table = context.workbook.tables.getItemOrNullObject(tableName);
      await context.sync();

      if (sheet.isNullObject) {
        sheet = context.workbook.worksheets.add(sheetName);
        sheet.visibility = Excel.SheetVisibility.hidden;
        await context.sync();
      }

      if (!table.isNullObject) {
        table.delete();
        await context.sync();
      }

      const headers = Object.keys(rows[0]);
      const headerRange = sheet.getRange(
        `${startCell}:${getColumnLetter(headers.length - 1)}${startCell.match(/\d+/)![0]}`
      );
      headerRange.values = [headers];
      table = sheet.tables.add(headerRange, true);
      table.name = tableName;
      await context.sync();

      // Format text columns before inserting data
      for (let i = 0; i < leftColumnsAsText; i++) {
        const headerIndex = headers.indexOf(headers[i]);
        if (headerIndex !== -1) {
          const column = table.columns.getItemAt(headerIndex);
          const columnRange = column.getDataBodyRange();
          columnRange.getEntireColumn().numberFormat = [["@"]];
        }
      }
      await context.sync();
      const dataToInsert = rows.map((row) =>
        headers.map((name) => {
          const value = row[name];
          if (value === null || value === undefined) return "";
          if (value instanceof Date) return value.toISOString();
          if (typeof value === "object") return JSON.stringify(value);
          return String(value);
        })
      );

      const startRow = parseInt(startCell.match(/\d+/)![0], 10) + 1;
      const dataStartCell = `${startCell.match(/[A-Z]+/)}${startRow}`;
      const dataEndCell = `${getColumnLetter(headers.length - 1)}${rows.length + 1}`;
      const dataRange = sheet.getRange(`${dataStartCell}:${dataEndCell}`);
      dataRange.values = dataToInsert;
      await context.sync();
    });
  } catch (error) {
    // ???: Change to logger.warn?
    console.error("Error transforming data or loading into sheet:", error);
  }
}

export const readableSortFieldLabel = (sortField: string) => {
  if (sortField.startsWith("label_")) {
    return sortField.substring(6) + " - Labels";
  }
  const prefix = sortField.substring(0, 2);
  const rest = sortField.substring(2);
  if ((prefix === "uf" || prefix === "mf") && !isNaN(Number(rest))) {
    return prefix.toUpperCase() + rest;
  }
  return sortField;
};

export const getTableTopLeftCellValue = async (chartName: string): Promise<string> => {
  try {
    return Excel.run(async (context) => {
      const table = context.workbook.tables.getItem(chartName);
      const topLeftCell = table.getRange().getCell(0, 0);
      topLeftCell.load("values");
      await context.sync();
      return topLeftCell.values[0][0];
    });
  } catch (error) {
    return "";
  }
};

export const optionsAsEventData = (options: Record<string, ChartOption>): Record<string, string> => {
  return Object.fromEntries(Object.entries(options).map(([key, option]) => [key, option.selectedOptionItem.key]));
};
